import { CHRONIC_REASON_OPTIONS, formatChronicDescription, isChronicBillingEventBilled, parseChronicDescription } from "../../../utils/chronicBilling";
import { useState } from "react";
import { API } from "../../../services/api";
import { DateField, Modal, TextField } from "./AdministrationFields";
import { ADMIN_API_CONFIG, EVENT_LABELS } from "../model/administrationConstants";
import { formatDate, formatPatientName } from "../model/administrationRules";
import {
    chronicCommentFieldStyle,
    chronicHistoryActionsStyle,
    chronicHistoryCommentStyle,
    chronicHistoryProductInfoStyle,
    chronicProductCommentTextareaStyle,
    chronicReasonFieldStyle,
    dangerButtonStyle,
    emptyStyle,
    errorStyle,
    historyRowStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/administrationModalStyles";
import { inputStyle, labelStyle } from "../styles/administrationStyles";

// Une date de l'historique ; pour la facturation chronique, le motif et le commentaire restent modifiables.
function EventHistoryRow({ event, chronic, saving, onReasonChange, onCommentChange, onEdit, onRemove }) {
    const chronicInfo = chronic ? parseChronicDescription(event.description) : null;
    return (
        <div style={historyRowStyle}>
            <div style={{ minWidth: 0 }}>
                <strong>{formatDate(event.date)}</strong>
                {chronic ? (
                    <div style={chronicHistoryProductInfoStyle}>
                        {chronicInfo.productInfo || "Produit sans information"}
                        {chronicInfo.comment && (
                            <div style={chronicHistoryCommentStyle}>
                                Commentaire : {chronicInfo.comment}
                            </div>
                        )}
                    </div>
                ) : event.description && (
                    <div style={{ color: "#4b5563", marginTop: "3px" }}>{event.description}</div>
                )}
                <small style={{ color: "#9ca3af" }}>Ajouté par {event.createdBy || "utilisateur inconnu"}</small>
            </div>
            <div style={chronic ? chronicHistoryActionsStyle : { display: "flex", gap: "7px", alignItems: "center" }}>
                {chronic && (
                    <label style={chronicReasonFieldStyle}>
                        <span>Motif</span>
                        <select
                            value={chronicInfo.reason}
                            onChange={selectEvent => onReasonChange(event, selectEvent.target.value)}
                            disabled={saving}
                            style={inputStyle}
                        >
                            <option value="">—</option>
                            {CHRONIC_REASON_OPTIONS.filter(Boolean).map(reason => (
                                <option key={reason} value={reason}>{reason}</option>
                            ))}
                        </select>
                    </label>
                )}
                {chronic && (
                    <label style={chronicCommentFieldStyle}>
                        <span>Commentaire</span>
                        <textarea
                            defaultValue={chronicInfo.comment}
                            onBlur={blurEvent => onCommentChange(event, blurEvent.target.value)}
                            disabled={saving}
                            placeholder="Commentaire produit"
                            style={chronicProductCommentTextareaStyle}
                        />
                    </label>
                )}
                <div style={{ display: "flex", gap: "7px", alignItems: "center" }}>
                    <button type="button" onClick={() => onEdit(event)} style={secondaryButtonStyle}>Modifier</button>
                    <button type="button" onClick={() => onRemove(event.id)} style={dangerButtonStyle}>Supprimer</button>
                </div>
            </div>
        </div>
    );
}

export function EventsModal({ config, onClose, onChanged }) {
    const { row, type, vaccination } = config;
    const [date, setDate] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);
    const [savingReasonId, setSavingReasonId] = useState(null);
    const [error, setError] = useState("");
    const [editingEventId, setEditingEventId] = useState(null);
    const events = (vaccination ? row.vaccinationEvents : row.billingEvents)
        .filter(event => event.type === type)
        .filter(event => type !== "CHRONIC" || isChronicBillingEventBilled(event));

    const addEvent = async event => {
        event.preventDefault();
        if (!date) return;
        setSaving(true);
        setError("");
        try {
            const suffix = vaccination ? "vaccination-events" : "billing-events";
            const payload = { type, date, description };
            if (editingEventId) {
                await API.put(`/administration/${suffix}/${editingEventId}`, payload, ADMIN_API_CONFIG);
            } else {
                await API.post(`/administration/patients/${row.patientId}/${suffix}`, payload, ADMIN_API_CONFIG);
            }
            await onChanged();
            setDate("");
            setDescription("");
            setEditingEventId(null);
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'ajouter cette date.");
        } finally {
            setSaving(false);
        }
    };

    const startEditingEvent = event => {
        setEditingEventId(event.id);
        setDate(event.date);
        setDescription(event.description || "");
        setError("");
    };

    const cancelEditingEvent = () => {
        setEditingEventId(null);
        setDate("");
        setDescription("");
    };

    const removeEvent = async eventId => {
        setError("");
        try {
            const prefix = vaccination ? "vaccination-events" : "billing-events";
            await API.delete(`/administration/${prefix}/${eventId}`, ADMIN_API_CONFIG);
            await onChanged();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible de supprimer cette date.");
        }
    };

    const updateChronicReason = async (billingEvent, reason) => {
        const { productInfo, medicamentId, comment } = parseChronicDescription(billingEvent.description);
        setSavingReasonId(billingEvent.id);
        setError("");
        try {
            await API.put(`/administration/billing-events/${billingEvent.id}`, {
                type,
                date: billingEvent.date,
                description: formatChronicDescription(productInfo || billingEvent.description, reason, medicamentId, comment),
            }, ADMIN_API_CONFIG);
            await onChanged();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'enregistrer ce motif.");
        } finally {
            setSavingReasonId(null);
        }
    };

    const updateChronicComment = async (billingEvent, comment) => {
        const { productInfo, medicamentId, reason, comment: currentComment } = parseChronicDescription(billingEvent.description);
        if ((currentComment || "") === String(comment || "").trim()) return;
        setSavingReasonId(billingEvent.id);
        setError("");
        try {
            await API.put(`/administration/billing-events/${billingEvent.id}`, {
                type,
                date: billingEvent.date,
                description: formatChronicDescription(productInfo || billingEvent.description, reason, medicamentId, comment),
            }, ADMIN_API_CONFIG);
            await onChanged();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'enregistrer ce commentaire.");
        } finally {
            setSavingReasonId(null);
        }
    };

    return (
        <Modal title={`${EVENT_LABELS[type]} — ${formatPatientName(row)}`} onClose={onClose}>
            <form onSubmit={addEvent} style={{ display: "grid", gridTemplateColumns: "160px 1fr auto", gap: "10px", alignItems: "end" }}>
                <label style={labelStyle}>Date<DateField value={date} onChange={setDate} /></label>
                <label style={labelStyle}>Information complémentaire<TextField value={description} onChange={setDescription} /></label>
                <div style={{ display: "flex", gap: "7px" }}>
                    <button type="submit" disabled={saving || !date} style={primaryButtonStyle}>
                        {saving ? "Enregistrement…" : editingEventId ? "Enregistrer" : "Ajouter"}
                    </button>
                    {editingEventId && <button type="button" onClick={cancelEditingEvent} style={secondaryButtonStyle}>Annuler</button>}
                </div>
            </form>
            {error && <p style={errorStyle}>{error}</p>}
            <div style={{ marginTop: "22px", maxHeight: "340px", overflowY: "auto" }}>
                {events.length === 0 ? (
                    <p style={emptyStyle}>Aucun historique.</p>
                ) : events.map(event => (
                    <EventHistoryRow
                        key={event.id}
                        event={event}
                        chronic={!vaccination && type === "CHRONIC"}
                        saving={savingReasonId === event.id}
                        onReasonChange={updateChronicReason}
                        onCommentChange={updateChronicComment}
                        onEdit={startEditingEvent}
                        onRemove={removeEvent}
                    />
                ))}
            </div>
        </Modal>
    );
}
